import { SdkContext } from '../internal'
import { versionedSettingsWrite } from '@/lib/realtime/settingsVersion'
import type { NodeVariables, Workflow, WorkflowCatalog, WorkflowGraph, WorkflowInput, WorkflowManualRun, WorkflowRun, WorkflowRunStatus, WorkflowRunsPage, WorkflowSenders, WorkflowSendersScope, WorkflowSimulation, WorkflowSummary, WorkflowTemplate, WorkflowWebhook, WorkflowWebhookDelivery } from '@/types/workflows'
import type { CampaignAudience } from '@/types/campaigns'

/** F-141: workflows. Owner and admin write; moderator and finance read; members get 403. */
export class WorkflowsResource {
  constructor(private ctx: SdkContext) {}

  async catalog(): Promise<WorkflowCatalog> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/triggers' })
  }

  /**
   * The workflows, each with `canEdit`/`canActivate`/`canDelete`/`canStop`, whether this person may create one,
   * how many are on and the plan's ceiling (`maxActive`, null = unlimited). `since` counts only the runs of the period.
   */
  async list(params: { status?: 'DRAFT' | 'ACTIVE' | 'PAUSED'; since?: '7d' | '30d' } = {}): Promise<{ items: Workflow[]; canCreate: boolean; activeCount: number | null; maxActive: number | null }> {
    const res = await this.ctx.envelope<{ data?: Workflow[]; permissions?: { canCreate?: boolean }; activeCount?: number; maxActive?: number | null }>({ httpMethod: 'GET', path: '/workflows' }, { query: params })
    return {
      items: Array.isArray(res?.data) ? res.data : [],
      canCreate: Boolean(res?.permissions?.canCreate),
      activeCount: typeof res?.activeCount === 'number' ? res.activeCount : null,
      maxActive: typeof res?.maxActive === 'number' ? res.maxActive : null
    }
  }

  /** Phase 5d: success rate over 7 and 30 days, skipped steps, error logs, steps waiting for quota and the last 10 errors. */
  async summary(id: string): Promise<WorkflowSummary> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/{id}/summary' }, { id })
  }

  /* ---------------------------------------------------------------- F-141 phase 3 */

  /** Who a DM, comment or reaction may go out as (phase 5g: the whole team, or only the owner, per `scope`). */
  async senders(): Promise<WorkflowSenders> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/senders' })
  }

  /** Phase 5g, owner and admin: on whose behalf workflows may send DMs, comment and react (`team` by default, or `owner`). */
  async settings(): Promise<{ senders: WorkflowSendersScope }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/settings' })
  }

  async updateSettings(body: { senders: WorkflowSendersScope }): Promise<{ senders: WorkflowSendersScope }> {
    return versionedSettingsWrite((v) => this.ctx.call<{ senders: WorkflowSendersScope }>({ httpMethod: 'PUT', path: '/workflows/settings', hasBody: true }, { body: { ...body, ...(v ? { settingsUpdatedAt: v } : {}) } }))
  }

  async templates(): Promise<WorkflowTemplate[]> {
    const r = await this.ctx.call<{ templates?: WorkflowTemplate[] }>({ httpMethod: 'GET', path: '/workflows/templates' })
    return r?.templates ?? []
  }

  /** A draft from a template (its e-mails created in the library); nothing is sent. */
  async createFromTemplate(key: string): Promise<Workflow> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/templates/{key}', hasBody: true }, { key, body: {} })
  }

  async get(id: string): Promise<Workflow> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/{id}' }, { id })
  }

  /** `triggerType` only for a graph without trigger nodes (2026-10-26). */
  async create(body: WorkflowInput & { name: string; triggerType?: string; graph: WorkflowGraph }): Promise<Workflow> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows', hasBody: true }, { body })
  }

  /** Active or paused: the graph becomes a new version for whoever enters next. */
  /** `updatedAt`: the version read (409 with `data.updatedAt` when another save came first). */
  async update(id: string, body: WorkflowInput & { updatedAt?: string }): Promise<Workflow> {
    return this.ctx.call({ httpMethod: 'PATCH', path: '/workflows/{id}', hasBody: true }, { id, body })
  }

  async remove(id: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/workflows/{id}' }, { id })
  }

  /** Sends e-mail from the next triggers on (never retroactive). */
  async activate(id: string, consent = false, includeExisting = false): Promise<Workflow> {
    /* A time trigger reaches people already in the base: the first activation asks `consent: true` (422 `workflows.consentRequired`).
       `includeExisting` (time.no_access) also takes, once, who is already past N days, and asks the consent every time. */
    const body = { ...(consent || includeExisting ? { consent: true } : {}), ...(includeExisting ? { includeExisting: true } : {}) }
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/activate', hasBody: true }, { id, body })
  }

  /** Turn on (a time trigger's first activation carries `consent: true`) or resume. */
  async activateOrResume(kind: 'activate' | 'resume', id: string, consent: boolean, includeExisting = false): Promise<Workflow> {
    return kind === 'activate' ? this.activate(id, consent, includeExisting) : this.resume(id)
  }

  async pause(id: string): Promise<Workflow> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/pause', hasBody: true }, { id, body: {} })
  }

  /** Stop: the workflow pauses and every run under way is canceled (nothing waiting is sent); answers `canceledRuns`. */
  async stop(id: string): Promise<Workflow & { canceledRuns?: number }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/stop', hasBody: true }, { id, body: {} })
  }

  async resume(id: string): Promise<Workflow> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/resume', hasBody: true }, { id, body: {} })
  }

  async runs(id: string, params: { status?: WorkflowRunStatus; page?: number; limit?: number; from?: string; to?: string; q?: string } = {}): Promise<{ data: WorkflowRunsPage; pagination: { page: number; totalPages: number } | null }> {
    const res = await this.ctx.envelope<{ data: WorkflowRunsPage; pagination?: { page: number; totalPages: number } }>({ httpMethod: 'GET', path: '/workflows/{id}/runs' }, { id, query: params })
    return { data: res.data, pagination: res.pagination ?? null }
  }

  async run(runId: string): Promise<WorkflowRun> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/runs/{runId}' }, { runId })
  }

  /** Hides one finished run from the history (409 `workflows.runNotFinished` while it runs or waits). Owner/admin. */
  async deleteRun(runId: string): Promise<unknown> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/workflows/runs/{runId}' }, { runId })
  }

  /** Finished runs up to `before` (or all): without `confirm` only counts, with it hides them. Owner/admin. */
  async clearRuns(id: string, body: { before?: string; confirm?: boolean }): Promise<{ count: number; deleted?: number }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/runs/clear', hasBody: true }, { id, body })
  }

  async cancelRun(runId: string): Promise<WorkflowRun> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/runs/{runId}/cancel', hasBody: true }, { runId, body: {} })
  }

  /** Paged (`page`, `limit` up to 50). */
  async memberHistory(memberId: string, params: { page?: number; limit?: number } = {}): Promise<{ data: { memberId: string; runs: WorkflowRun[] }; pagination: { page: number; totalPages: number } | null }> {
    const res = await this.ctx.envelope<{ data: { memberId: string; runs: WorkflowRun[] }; pagination?: { page: number; totalPages: number } }>({ httpMethod: 'GET', path: '/workflows/members/{memberId}/history' }, { memberId, query: params })
    return { data: res.data, pagination: res.pagination ?? null }
  }

  /** How many different people fired the trigger (with its filter) in the last 1, 7 and 30 days. */
  async estimate(id: string): Promise<{ people: { last1Day: number | null; last7Days: number | null; last30Days: number | null; today?: number; existing?: number | null } }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/{id}/estimate' }, { id })
  }

  /** Nothing is sent. An unsaved `graph` is owner/admin only (SEC-244). */
  /** `sendEmailsToMe`: the e-mails it would send go to the signed-in person only, "[Teste]" in the subject (10 per call, 10 calls an hour). */
  async simulate(id: string, memberId: string, graph?: WorkflowGraph, triggerNodeId?: string | null, sendEmailsToMe = false): Promise<WorkflowSimulation> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/simulate', hasBody: true }, { id, body: { memberId, ...(graph ? { graph } : {}), ...(triggerNodeId ? { triggerNodeId } : {}), ...(sendEmailsToMe ? { sendEmailsToMe: true } : {}) } })
  }

  /**
   * Manual trigger: starts the workflow for chosen people or a campaign audience. Without `confirm`
   * only counts; with it, `consent` and an `idempotencyKey` (the same key never starts twice).
   */
  async runNow(id: string, body: { memberIds?: string[]; audience?: CampaignAudience; triggerNodeId?: string; confirm?: boolean; consent?: boolean; idempotencyKey?: string }): Promise<WorkflowManualRun> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/run', hasBody: true }, { id, body })
  }

  /** A webhook trigger's address (never the token): null until created. `nodeId` is the trigger node of the saved draft. */
  async webhook(id: string, nodeId: string): Promise<WorkflowWebhook | null> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/{id}/triggers/{nodeId}/webhook' }, { id, nodeId })
  }

  /** Creates or rotates the address: the token and the secret come only in this answer. */
  async issueWebhook(id: string, nodeId: string, hmac = true): Promise<WorkflowWebhook> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/triggers/{nodeId}/webhook', hasBody: true }, { id, nodeId, body: { hmac } })
  }

  async revokeWebhook(id: string, nodeId: string): Promise<WorkflowWebhook> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/workflows/{id}/triggers/{nodeId}/webhook' }, { id, nodeId })
  }

  /** The calls the workflow's webhooks received, newest first (never the body). */
  async webhookDeliveries(id: string, page = 1): Promise<{ items: WorkflowWebhookDelivery[]; pagination: { page: number; totalPages?: number; pages?: number } | null }> {
    const res = await this.ctx.envelope<{ data: WorkflowWebhookDelivery[]; pagination?: { page: number; pages?: number } }>({ httpMethod: 'GET', path: '/workflows/{id}/webhook-deliveries' }, { id, query: { page } })
    return { items: res.data ?? [], pagination: res.pagination ?? null }
  }

  /** Phase 5b: the variables that reach a node of the saved version, by source block; with `text`, the text filled with the examples. */
  async variables(id: string, params: { nodeId: string; text?: string; channel?: 'dm' | 'html' | 'slack' }): Promise<NodeVariables> {
    return this.ctx.call({ httpMethod: 'GET', path: '/workflows/{id}/variables' }, { id, query: params })
  }

  /** The same for the drawing in the editor (not saved); nothing is stored. */
  async draftVariables(body: { graph: WorkflowGraph; nodeId: string; text?: string; channel?: 'dm' | 'html' | 'slack'; triggerType?: string; triggerFilter?: unknown }): Promise<NodeVariables> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/variables', hasBody: true }, { body })
  }

  /** Sends that node's e-mail only to the signed-in person (10 an hour). */
  async test(id: string, nodeId: string, language?: string): Promise<{ sentTo: string; language?: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/workflows/{id}/test', hasBody: true }, { id, body: { nodeId, ...(language ? { language } : {}) } })
  }
}
